/**
 * BarChart — the larger sibling of Sparkline for the expanded row.
 *
 * Tufte: same data-ink discipline as Sparkline, but with two concessions
 * needed at this size — date labels at endpoints and value labels above
 * bars when count <= 12. Above that we let the eye scan unlabeled.
 *
 * Props:
 *   points: [{ game_date, actual, hit }]   chronological
 *   line?:  number   reference line drawn dashed across
 *   height?: number  default 80
 *   showValues?: boolean  default true if count <= 12
 */

import React from 'react';

const COLORS = {
  hit:   '#22c55e',
  miss:  '#475569',
  empty: '#1e293b',
  line:  '#94a3b8',
  axis:  '#475569',
  text:  '#94a3b8',
};

function fmtShort(d) {
  // "2026-06-04" -> "6/4"
  if (!d) return '';
  const parts = d.split('-');
  return `${Number(parts[1])}/${Number(parts[2])}`;
}

export default function BarChart({
  points = [],
  line = null,
  height = 80,
  showValues = null,   // null = auto by length
}) {
  if (!points || points.length === 0) {
    return (
      <div className="text-xs text-text-secondary opacity-60 py-3 text-center">
        No game history yet.
      </div>
    );
  }

  const N = points.length;
  const width = Math.max(220, N * 14);
  const padL = 4;
  const padR = 4;
  const padTop = 14;     // headroom for value labels
  const padBot = 16;     // room for date labels
  const usableW = width - padL - padR;
  const usableH = height - padTop - padBot;
  const slot = usableW / N;
  const barW = Math.max(4, slot * 0.72);

  const max = Math.max(1, ...points.map((p) => p.actual ?? 0));
  const showVals = showValues === null ? N <= 12 : showValues;

  // Reference line
  const lineY =
    line !== null && max > 0
      ? padTop + usableH - (line / max) * usableH
      : null;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {/* baseline */}
      <line
        x1={padL} y1={padTop + usableH}
        x2={width - padR} y2={padTop + usableH}
        stroke={COLORS.axis} strokeWidth={0.6} opacity={0.6}
      />
      {/* reference line */}
      {lineY !== null && (
        <line
          x1={padL} y1={lineY}
          x2={width - padR} y2={lineY}
          stroke={COLORS.line} strokeWidth={0.7}
          strokeDasharray="3,2" opacity={0.7}
        />
      )}
      {/* bars */}
      {points.map((p, i) => {
        const v = p.actual ?? 0;
        const bh = Math.max(1.5, (v / max) * usableH);
        const x = padL + i * slot + (slot - barW) / 2;
        const y = padTop + (usableH - bh);
        const color = v === 0 ? COLORS.empty : p.hit ? COLORS.hit : COLORS.miss;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={color} rx={1}>
              <title>{`${p.game_date}: ${v}${p.hit ? ' ✓' : ''}`}</title>
            </rect>
            {showVals && v > 0 && (
              <text
                x={x + barW / 2}
                y={y - 2}
                fill={COLORS.text}
                fontSize="8"
                textAnchor="middle"
              >
                {Number.isInteger(v) ? v : v.toFixed(1)}
              </text>
            )}
          </g>
        );
      })}
      {/* date labels at endpoints + midpoint */}
      <text
        x={padL}
        y={height - 4}
        fill={COLORS.text}
        fontSize="8"
        textAnchor="start"
      >
        {fmtShort(points[0]?.game_date)}
      </text>
      {N > 5 && (
        <text
          x={padL + usableW / 2}
          y={height - 4}
          fill={COLORS.text}
          fontSize="8"
          textAnchor="middle"
        >
          {fmtShort(points[Math.floor(N / 2)]?.game_date)}
        </text>
      )}
      <text
        x={width - padR}
        y={height - 4}
        fill={COLORS.text}
        fontSize="8"
        textAnchor="end"
      >
        {fmtShort(points[N - 1]?.game_date)}
      </text>
    </svg>
  );
}
